import { FC, ReactNode, useState } from "react"
import { Check, ChevronDown, Copy } from "lucide-react"

/**
 * Canonical pending_subscription.converted outgoing payload. Single source of
 * truth — this exact string is rendered (with syntax highlighting) AND copied
 * to clipboard. Shape mirrors the live payload produced by the backend when a
 * pending subscription converts.
 */
const SCHEMA_JSON = `{
  "id": 42,
  "loid": 1930,
  "email": "buyer@example.com",
  "first_name": "Jane",
  "last_name": "Doe",
  "phone": "+15551234567",
  "source": "connector",
  "quote_info": {
    "property_type": "single_family_home",
    "list_price": "500000",
    "loan_amount": "400000",
    "loan_term": "30",
    "loan_type": "conventional",
    "location": {
      "state": "SC",
      "city": "Summerville",
      "zipcode": "29485",
      "street_address": "123 Main St"
    },
    "nmls": "1234567",
    "residency_type": "primary_home",
    "loan_purpose": "purchase",
    "credit_score": "740"
  },
  "settings": [],
  "trigger_rate": 7.5,
  "status": 0,
  "subscription_id": 100,
  "converted_at": 1700000000,
  "last_priced_at": 1699999800,
  "next_run_at": 1700001200,
  "price_attempts": 1,
  "last_quote_id": 500,
  "last_quote_rate": 6.875,
  "last_quote_apr": 7.015,
  "client_request_id": null,
  "created": 1699990000,
  "updated": 1700000000
}`

/**
 * Lightweight JSON syntax highlighter. Distinguishes keys (string + colon),
 * string values, numbers, booleans/null, and punctuation. Returns an array of
 * React nodes ready to drop inside a <pre>.
 */
const highlightJson = (json: string): ReactNode[] => {
  const tokens: ReactNode[] = []
  const regex =
    /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|(-?\d+(?:\.\d+)?)|([{}[\],])/g
  let lastIndex = 0
  let key = 0
  let match: RegExpExecArray | null

  while ((match = regex.exec(json)) !== null) {
    if (match.index > lastIndex) {
      tokens.push(json.slice(lastIndex, match.index))
    }
    const [, str, colon, bool, num, punct] = match
    if (str !== undefined) {
      if (colon !== undefined) {
        tokens.push(
          <span key={key++} className="text-sky-300">
            {str}
          </span>
        )
        tokens.push(
          <span key={key++} className="text-zinc-500">
            {colon}
          </span>
        )
      } else {
        tokens.push(
          <span key={key++} className="text-amber-200">
            {str}
          </span>
        )
      }
    } else if (bool !== undefined) {
      tokens.push(
        <span key={key++} className="text-violet-300">
          {bool}
        </span>
      )
    } else if (num !== undefined) {
      tokens.push(
        <span key={key++} className="text-emerald-300">
          {num}
        </span>
      )
    } else if (punct !== undefined) {
      tokens.push(
        <span key={key++} className="text-zinc-500">
          {punct}
        </span>
      )
    }
    lastIndex = regex.lastIndex
  }
  if (lastIndex < json.length) {
    tokens.push(json.slice(lastIndex))
  }
  return tokens
}

const HIGHLIGHTED_SCHEMA = highlightJson(SCHEMA_JSON)

const PendingSubscriptionSchemaPreview: FC = () => {
  const [copied, setCopied] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(SCHEMA_JSON)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      /* clipboard unavailable — fail silently */
    }
  }

  return (
    <div className="border-t pt-4">
      <div className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950 shadow-sm animate-fade-slide-in">
        {/* Header */}
        <div
          className={`flex items-start justify-between gap-3 px-4 py-3 ${
            expanded ? "border-b border-zinc-800/80" : ""
          }`}
        >
          <button
            type="button"
            onClick={() => setExpanded(prev => !prev)}
            aria-expanded={expanded}
            className="flex flex-1 items-start gap-3 text-left transition-opacity hover:opacity-90"
          >
            <ChevronDown
              className={`mt-1 h-4 w-4 shrink-0 text-zinc-500 transition-transform duration-200 ${
                expanded ? "rotate-0" : "-rotate-90"
              }`}
            />
            <div className="mt-1 h-8 w-0.5 rounded-full bg-sky-400" />
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-medium tracking-wide text-zinc-100">
                  Payload schema
                </h4>
                <span className="rounded border border-sky-400/30 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-sky-300/90">
                  POST · JSON
                </span>
              </div>
              <p className="mt-0.5 text-xs text-zinc-400">
                {expanded
                  ? "Sent to your CRM webhook when a pending subscription converts."
                  : "Click to view the JSON payload sent to your webhook."}
              </p>
            </div>
          </button>
          {expanded && (
            <button
              type="button"
              onClick={handleCopy}
              className="flex shrink-0 items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-2.5 py-1 text-xs font-medium text-zinc-300 transition hover:bg-zinc-800 hover:text-zinc-100"
            >
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5 text-emerald-400" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" />
                  Copy
                </>
              )}
            </button>
          )}
        </div>

        {expanded && (
          <>
            {/* Code */}
            <pre className="max-h-[28rem] overflow-auto px-5 py-4 font-mono text-[12.5px] leading-6 text-zinc-300 animate-fade-slide-in">
              <code>{HIGHLIGHTED_SCHEMA}</code>
            </pre>

            {/* Footer */}
            <div className="border-t border-zinc-800/80 bg-zinc-900/40 px-4 py-3">
              <p className="text-xs text-zinc-400">
                <span className="font-medium text-zinc-200">
                  No template needed.
                </span>{" "}
                Pending subscription connectors send the full payload above when
                a conversion occurs — field mapping isn&apos;t required.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default PendingSubscriptionSchemaPreview
